import { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { useConfirm } from "@/components/ConfirmDialog";
import { useAuth } from "@/App";
import { Layout } from "@/components/Layout";
import { RichTextEditor } from "@/components/RichTextEditor";
import { ResizablePanelGroup, ResizablePanel, ResizableHandle } from "@/components/ui/resizable";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import EntityContextMenu from "@/components/EntityContextMenu";
import {
  Plus, BookOpen, FileText, Trash2, MessageSquarePlus, Wand2,
  Send, Loader2, Sparkles, Search, NotebookPen, ChevronRight, Pin, Mic, GripVertical
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SECTION_COLORS = ["#ef4444", "#f59e0b", "#10b981", "#3b82f6", "#8b5cf6", "#ec4899", "#06b6d4", "#facc15"];
const PIN_KEY = (cid) => `np_pinned_${cid}`;
const RECENT_KEY = (cid) => `np_recent_${cid}`;

const StoryNotebookPage = () => {
  const { campaignId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isCommoner = (user?.tier || "commoner") === "commoner";
  const confirm = useConfirm();

  const [campaign, setCampaign] = useState(null);
  const [stories, setStories] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [selectedStory, setSelectedStory] = useState(null);
  const [selectedSession, setSelectedSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");

  // Add-story / Add-session dialog
  const [createDialog, setCreateDialog] = useState(null); // 'story' | 'session' | null
  const [createMode, setCreateMode] = useState("manual"); // 'manual' | 'ai'
  const [createForm, setCreateForm] = useState({ title: "", content: "" });
  const [creating, setCreating] = useState(false);

  // Edit chapter content (right pane)
  const [editingStoryContent, setEditingStoryContent] = useState("");
  const [storyContentDirty, setStoryContentDirty] = useState(false);
  const [editingSessionContent, setEditingSessionContent] = useState("");
  const [sessionContentDirty, setSessionContentDirty] = useState(false);

  // Drag & drop reorder state
  const [dragKind, setDragKind] = useState(null); // "story" | "session" | null
  const [dragId, setDragId] = useState(null);
  const [dragOverId, setDragOverId] = useState(null);

  // ============ REORDER HANDLERS ============
  const reorderStories = async (fromId, toId) => {
    if (!fromId || !toId || fromId === toId) return;
    const ids = stories.map(s => s.id);
    const from = ids.indexOf(fromId);
    const to = ids.indexOf(toId);
    if (from < 0 || to < 0) return;
    const next = [...stories];
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    setStories(next);
    try {
      await axios.post(`${API}/campaigns/${campaignId}/chapters/reorder`, { ids: next.map(s => s.id) }, { withCredentials: true });
    } catch {
      toast.error("Failed to save order");
      fetchInitial(); // roll back to server truth
    }
  };

  const reorderSessions = async (fromId, toId) => {
    if (!fromId || !toId || fromId === toId || !selectedStory) return;
    const ids = sessions.map(s => s.id);
    const from = ids.indexOf(fromId);
    const to = ids.indexOf(toId);
    if (from < 0 || to < 0) return;
    const next = [...sessions];
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    setSessions(next);
    try {
      await axios.post(`${API}/campaigns/${campaignId}/chapters/${selectedStory.id}/sessions/reorder`, { ids: next.map(s => s.id) }, { withCredentials: true });
    } catch {
      toast.error("Failed to save order");
    }
  };
  const sessionSaveTimer = useRef(null);
  const saveTimer = useRef(null);

  // Session entries
  const [sessionEntryDraft, setSessionEntryDraft] = useState("");
  const [postingEntry, setPostingEntry] = useState(false);
  const [recapBusy, setRecapBusy] = useState(false);

  // Resizable layout: persist sizes
  const [panelSizes, setPanelSizes] = useState(() => {
    try { return JSON.parse(localStorage.getItem("np_story_panels")) || [20, 25, 55]; } catch { return [20, 25, 55]; }
  });
  const onLayoutChange = (sizes) => {
    setPanelSizes(sizes);
    localStorage.setItem("np_story_panels", JSON.stringify(sizes));
  };

  // ===== Load campaign + stories =====
  const fetchInitial = useCallback(async () => {
    setLoading(true);
    try {
      const [campRes, chapRes] = await Promise.all([
        axios.get(`${API}/campaigns/${campaignId}`, { withCredentials: true }),
        axios.get(`${API}/campaigns/${campaignId}/chapters`, { withCredentials: true }),
      ]);
      setCampaign(campRes.data);
      const list = chapRes.data || [];
      setStories(list);
      if (list.length > 0 && !selectedStory) {
        setSelectedStory(list[0]);
      }
    } catch {
      toast.error("Failed to load story");
    } finally {
      setLoading(false);
    }
  }, [campaignId]);

  useEffect(() => { fetchInitial(); }, [fetchInitial]);

  // ===== Load sessions for selected story =====
  useEffect(() => {
    if (!selectedStory) { setSessions([]); return; }
    setEditingStoryContent(selectedStory.content || "");
    setStoryContentDirty(false);
    setSelectedSession(null);
    pushRecent(selectedStory);
    (async () => {
      try {
        const res = await axios.get(`${API}/campaigns/${campaignId}/chapters/${selectedStory.id}/sessions`, { withCredentials: true });
        setSessions(res.data || []);
      } catch {
        setSessions([]);
      }
    })();
  }, [selectedStory, campaignId]);

  // ===== Sync session rich-text body when user picks a session =====
  useEffect(() => {
    if (!selectedSession) return;
    setEditingSessionContent(selectedSession.content || "");
    setSessionContentDirty(false);
  }, [selectedSession?.id]); // eslint-disable-line

  const pushRecent = (story) => {
    try {
      const key = RECENT_KEY(campaignId);
      const cur = JSON.parse(localStorage.getItem(key)) || [];
      const entry = { path: "chapters", label: story.title || "Story", ts: Date.now() };
      const next = [entry, ...cur.filter(e => e.label !== entry.label)].slice(0, 8);
      localStorage.setItem(key, JSON.stringify(next));
      window.dispatchEvent(new Event("np-pinned-changed"));
    } catch {}
  };

  // ===== Story autosave (debounced) =====
  useEffect(() => {
    if (!storyContentDirty || !selectedStory) return;
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(async () => {
      try {
        await axios.put(`${API}/chapters/${selectedStory.id}`, {
          campaign_id: campaignId,
          title: selectedStory.title,
          content: editingStoryContent,
          order: selectedStory.order || 0,
          visibility: selectedStory.visibility || "dm_only",
          visible_to: selectedStory.visible_to || [],
        }, { withCredentials: true });
        setStoryContentDirty(false);
        setStories(prev => prev.map(s => s.id === selectedStory.id ? { ...s, content: editingStoryContent } : s));
      } catch {
        toast.error("Failed to save story");
      }
    }, 900);
    return () => { if (saveTimer.current) clearTimeout(saveTimer.current); };
  }, [editingStoryContent, storyContentDirty]); // eslint-disable-line

  // ===== Session autosave (debounced) — mirrors story autosave so session rich-text saves the same way =====
  useEffect(() => {
    if (!sessionContentDirty || !selectedSession) return;
    if (sessionSaveTimer.current) clearTimeout(sessionSaveTimer.current);
    sessionSaveTimer.current = setTimeout(async () => {
      try {
        await axios.put(`${API}/sessions/${selectedSession.id}`, {
          content: editingSessionContent,
        }, { withCredentials: true });
        setSessionContentDirty(false);
        setSessions(prev => prev.map(s => s.id === selectedSession.id ? { ...s, content: editingSessionContent } : s));
      } catch {
        toast.error("Failed to save session");
      }
    }, 900);
    return () => { if (sessionSaveTimer.current) clearTimeout(sessionSaveTimer.current); };
  }, [editingSessionContent, sessionContentDirty]); // eslint-disable-line

  // ===== Create story =====
  const openCreateStory = () => {
    setCreateForm({ title: "", content: "" });
    setCreateMode("manual");
    setCreateDialog("story");
  };

  const submitCreateStory = async () => {
    if (!createForm.title.trim()) { toast.error("Title required"); return; }
    setCreating(true);
    try {
      const res = await axios.post(`${API}/chapters`, {
        campaign_id: campaignId,
        title: createForm.title,
        content: createForm.content,
        order: stories.length,
        visibility: "dm_only",
        visible_to: [],
      }, { withCredentials: true });
      setStories(prev => [...prev, res.data]);
      setSelectedStory(res.data);
      setCreateDialog(null);
      toast.success("Story created");
    } catch {
      toast.error("Could not create story");
    } finally {
      setCreating(false);
    }
  };

  // ===== Create session (under story) =====
  const openCreateSession = () => {
    if (!selectedStory) { toast.error("Select a story first"); return; }
    setCreateForm({ title: `Session ${sessions.length + 1}`, content: "" });
    setCreateDialog("session");
  };

  // ===== Audio → transcript → AI summary → session =====
  const ingestAudioSession = async (file) => {
    if (!selectedStory) { toast.error("Pick a story first"); return; }
    if (file.size > 25 * 1024 * 1024) {
      toast.error("Audio must be 25 MB or smaller. Try compressing or splitting the recording.");
      return;
    }
    const fd = new FormData();
    fd.append("file", file);
    fd.append("title", file.name.replace(/\.[^.]+$/, ""));
    fd.append("chapter_id", selectedStory.id);
    const toastId = toast.loading("Transcribing & summarising — this can take a minute or two…");
    try {
      const res = await axios.post(
        `${API}/campaigns/${campaignId}/sessions/ingest-audio`,
        fd,
        { withCredentials: true, headers: { "Content-Type": "multipart/form-data" }, timeout: 300000 }
      );
      toast.dismiss(toastId);
      toast.success("Session created from recording");
      // Prepend to sessions list + open it
      setSessions(prev => [...prev, res.data]);
      setSelectedSession(res.data);
    } catch (err) {
      toast.dismiss(toastId);
      const detail = err.response?.data?.detail || err.message;
      toast.error(`Audio ingest failed: ${detail}`);
    }
  };

  const submitCreateSession = async () => {
    if (!createForm.title.trim() || !selectedStory) return;
    setCreating(true);
    try {
      const res = await axios.post(`${API}/sessions`, {
        campaign_id: campaignId,
        title: createForm.title,
        chapter_id: selectedStory.id,
        in_game_date: "",
      }, { withCredentials: true });
      setSessions(prev => [...prev, res.data]);
      setSelectedSession(res.data);
      setCreateDialog(null);
      toast.success("Session added");
    } catch {
      toast.error("Could not create session");
    } finally {
      setCreating(false);
    }
  };

  // ===== Delete story =====
  const deleteStory = async (story) => {
    const ok = await confirm("Delete Story?", `Remove "${story.title}" and detach its sessions?`);
    if (!ok) return;
    try {
      await axios.delete(`${API}/chapters/${story.id}`, { withCredentials: true });
      setStories(prev => prev.filter(s => s.id !== story.id));
      if (selectedStory?.id === story.id) setSelectedStory(stories.find(s => s.id !== story.id) || null);
      toast.success("Deleted");
    } catch { toast.error("Failed to delete"); }
  };

  // ===== Rename story (inline prompt) =====
  const renameStory = async (story) => {
    // eslint-disable-next-line no-alert
    const next = window.prompt("Rename story", story.title);
    if (!next || !next.trim() || next === story.title) return;
    try {
      await axios.put(`${API}/chapters/${story.id}`, { title: next.trim() }, { withCredentials: true });
      setStories(prev => prev.map(s => s.id === story.id ? { ...s, title: next.trim() } : s));
      if (selectedStory?.id === story.id) setSelectedStory(prev => prev ? { ...prev, title: next.trim() } : prev);
      toast.success("Renamed");
    } catch { toast.error("Failed to rename"); }
  };

  // ===== Delete session =====
  const deleteSession = async (session) => {
    const ok = await confirm("Delete Session?", `Remove "${session.title}"?`);
    if (!ok) return;
    try {
      await axios.delete(`${API}/sessions/${session.id}`, { withCredentials: true });
      setSessions(prev => prev.filter(s => s.id !== session.id));
      if (selectedSession?.id === session.id) setSelectedSession(null);
      toast.success("Deleted");
    } catch { toast.error("Failed to delete"); }
  };

  // ===== Add session entry =====
  const addSessionEntry = async () => {
    if (!selectedSession || !sessionEntryDraft.trim()) return;
    setPostingEntry(true);
    try {
      const res = await axios.post(`${API}/sessions/${selectedSession.id}/entries`, {
        type: "note",
        content: sessionEntryDraft,
      }, { withCredentials: true });
      // Backend returns just the new entry — append it to local state.
      const newEntry = res.data;
      setSelectedSession(prev => prev ? { ...prev, entries: [...(prev.entries || []), newEntry] } : prev);
      setSessions(prev => prev.map(s => s.id === selectedSession.id ? { ...s, entries: [...(s.entries || []), newEntry] } : s));
      setSessionEntryDraft("");
    } catch {
      toast.error("Failed to add entry");
    } finally {
      setPostingEntry(false);
    }
  };

  // ===== Generate session recap (AI) =====
  const generateRecap = async () => {
    if (!selectedSession || isCommoner) return;
    setRecapBusy(true);
    try {
      const res = await axios.post(`${API}/sessions/${selectedSession.id}/recap`, {}, { withCredentials: true });
      setSelectedSession(res.data);
      setSessions(prev => prev.map(s => s.id === res.data.id ? res.data : s));
      toast.success("Recap generated");
    } catch {
      toast.error("Failed to generate recap");
    } finally {
      setRecapBusy(false);
    }
  };

  // ===== Pin story to sidebar =====
  const pinStory = (story) => {
    try {
      const key = PIN_KEY(campaignId);
      const cur = JSON.parse(localStorage.getItem(key)) || [];
      const entry = { path: "chapters", label: story.title };
      const exists = cur.some(p => p.label === story.title);
      const next = exists ? cur.filter(p => p.label !== story.title) : [entry, ...cur].slice(0, 6);
      localStorage.setItem(key, JSON.stringify(next));
      window.dispatchEvent(new Event("np-pinned-changed"));
      toast.success(exists ? "Unpinned" : "Pinned to sidebar");
    } catch {}
  };

  const filteredStories = stories.filter(s => (s.title || "").toLowerCase().includes(search.toLowerCase()));

  if (loading) {
    return (
      <Layout campaignName={campaign?.name} hideBreadcrumbs>
        <div className="flex justify-center py-20"><div className="spinner" /></div>
      </Layout>
    );
  }

  const extraCrumbs = [];
  if (selectedStory) extraCrumbs.push({ label: selectedStory.title });
  if (selectedSession) extraCrumbs.push({ label: selectedSession.title });

  return (
    <Layout campaignName={campaign?.name} breadcrumbExtra={extraCrumbs}>
      <div className="rounded-sm border border-border bg-card overflow-hidden" style={{ height: "calc(100vh - 200px)", minHeight: 600 }} data-testid="story-notebook">
        <ResizablePanelGroup direction="horizontal" onLayout={onLayoutChange}>
          {/* ============== LEFT PANEL — STORIES ============== */}
          <ResizablePanel defaultSize={panelSizes[0]} minSize={15} maxSize={35}>
            <div className="h-full flex flex-col bg-[#110d0a]">
              <div className="px-3 py-2 border-b border-border flex items-center gap-2">
                <NotebookPen className="w-3.5 h-3.5 text-primary" />
                <h3 className="text-xs uppercase tracking-[0.18em] text-muted-foreground/80 flex-1">Stories</h3>
                <Button size="icon" variant="ghost" className="h-6 w-6" onClick={openCreateStory} data-testid="add-story-btn" title="Add Story">
                  <Plus className="w-3.5 h-3.5" />
                </Button>
              </div>
              <div className="p-2 border-b border-border/40">
                <div className="relative">
                  <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3 h-3 text-muted-foreground/60" />
                  <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search stories…" className="h-7 text-xs pl-7" data-testid="story-search" />
                </div>
              </div>
              <div className="flex-1 overflow-y-auto p-2 space-y-0.5">
                {filteredStories.length === 0 && (
                  <div className="text-center py-10 px-3">
                    <BookOpen className="w-8 h-8 text-muted-foreground/30 mx-auto mb-2" />
                    <p className="text-xs text-muted-foreground/70 mb-3">No stories yet. Start your campaign with one.</p>
                    <Button size="sm" onClick={openCreateStory} className="text-xs h-7" data-testid="add-first-story-btn">
                      <Plus className="w-3 h-3 mr-1" /> New Story
                    </Button>
                  </div>
                )}
                {filteredStories.map((s, idx) => {
                  const color = SECTION_COLORS[idx % SECTION_COLORS.length];
                  const active = selectedStory?.id === s.id;
                  const isDragOver = dragKind === "story" && dragOverId === s.id && dragId !== s.id;
                  return (
                    <EntityContextMenu
                      key={s.id}
                      onOpen={() => setSelectedStory(s)}
                      onRename={() => renameStory(s)}
                      onDelete={() => deleteStory(s)}
                      pinPath="chapters"
                      pinLabel={`Story: ${s.title}`}
                      entityName={s.title}
                    >
                    <div
                      draggable
                      onDragStart={(e) => { setDragKind("story"); setDragId(s.id); e.dataTransfer.effectAllowed = "move"; }}
                      onDragOver={(e) => { if (dragKind === "story") { e.preventDefault(); setDragOverId(s.id); } }}
                      onDragLeave={() => { if (dragOverId === s.id) setDragOverId(null); }}
                      onDrop={(e) => { e.preventDefault(); if (dragKind === "story") { reorderStories(dragId, s.id); } setDragId(null); setDragOverId(null); setDragKind(null); }}
                      onDragEnd={() => { setDragId(null); setDragOverId(null); setDragKind(null); }}
                      className={`${isDragOver ? "border-t-2 border-primary" : "border-t-2 border-transparent"}`}
                    >
                    <button
                      onClick={() => setSelectedStory(s)}
                      className={`group w-full text-left flex items-center gap-2 px-2 py-2 rounded-sm transition-colors ${active ? "bg-primary/15 text-foreground" : "hover:bg-muted/30 text-muted-foreground"} ${dragId === s.id ? "opacity-50" : ""}`}
                      data-testid={`story-list-item-${s.id}`}
                    >
                      <GripVertical className="w-3 h-3 flex-shrink-0 text-muted-foreground/30 cursor-grab active:cursor-grabbing" data-testid={`story-drag-handle-${s.id}`} />
                      <div className="w-1 h-6 rounded-sm flex-shrink-0" style={{ backgroundColor: color }} />
                      <BookOpen className={`w-3.5 h-3.5 flex-shrink-0 ${active ? "text-primary" : "text-muted-foreground/60"}`} />
                      <span className="flex-1 text-sm truncate">{s.title}</span>
                      <span
                        onClick={(e) => { e.stopPropagation(); pinStory(s); }}
                        className="opacity-0 group-hover:opacity-100 text-muted-foreground/50 hover:text-primary"
                        title="Pin to sidebar"
                        data-testid={`pin-story-${s.id}`}
                      >
                        <Pin className="w-3 h-3" />
                      </span>
                      <span
                        onClick={(e) => { e.stopPropagation(); deleteStory(s); }}
                        className="opacity-0 group-hover:opacity-100 text-muted-foreground/50 hover:text-destructive"
                        title="Delete story"
                        data-testid={`delete-story-${s.id}`}
                      >
                        <Trash2 className="w-3 h-3" />
                      </span>
                    </button>
                    </div>
                    </EntityContextMenu>
                  );
                })}
              </div>
            </div>
          </ResizablePanel>

          <ResizableHandle withHandle />

          {/* ============== MIDDLE PANEL — SESSIONS / PAGES ============== */}
          <ResizablePanel defaultSize={panelSizes[1]} minSize={18} maxSize={40}>
            <div className="h-full flex flex-col bg-[#14100c]">
              <div className="px-3 py-2 border-b border-border flex items-center gap-2">
                <FileText className="w-3.5 h-3.5 text-primary/80" />
                <h3 className="text-xs uppercase tracking-[0.18em] text-muted-foreground/80 flex-1 truncate">
                  {selectedStory ? selectedStory.title : "Sessions"}
                </h3>
                <Button size="icon" variant="ghost" className="h-6 w-6" onClick={openCreateSession} disabled={!selectedStory} data-testid="add-page-btn" title="Add Session">
                  <Plus className="w-3.5 h-3.5" />
                </Button>
                <label
                  className={`h-6 w-6 inline-flex items-center justify-center rounded-sm hover:bg-muted/30 cursor-pointer ${selectedStory ? "" : "opacity-40 pointer-events-none"}`}
                  title="Upload audio → AI summary (Whisper + Claude)"
                  data-testid="upload-audio-btn"
                >
                  <Mic className="w-3.5 h-3.5 text-primary" />
                  <input
                    type="file"
                    accept="audio/*"
                    className="hidden"
                    onChange={(e) => {
                      const f = e.target.files?.[0];
                      if (f) ingestAudioSession(f);
                      e.target.value = "";
                    }}
                  />
                </label>
              </div>
              <div className="flex-1 overflow-y-auto p-2">
                {!selectedStory && (
                  <p className="text-xs text-muted-foreground/60 italic text-center py-10">Pick a story to view its sessions.</p>
                )}
                {selectedStory && sessions.length === 0 && (
                  <div className="text-center py-10 px-3">
                    <FileText className="w-7 h-7 text-muted-foreground/30 mx-auto mb-2" />
                    <p className="text-xs text-muted-foreground/70 mb-3">No sessions yet under this story.</p>
                    <Button size="sm" onClick={openCreateSession} className="text-xs h-7" data-testid="add-first-session-btn">
                      <Plus className="w-3 h-3 mr-1" /> New Session
                    </Button>
                  </div>
                )}
                {/* "Overview" pseudo-page — selecting it shows the story content editor */}
                {selectedStory && (
                  <button
                    onClick={() => setSelectedSession(null)}
                    className={`w-full text-left flex items-center gap-2 px-2 py-1.5 rounded-sm transition-colors mb-1 ${!selectedSession ? "bg-primary/10 text-foreground" : "hover:bg-muted/30 text-muted-foreground"}`}
                    data-testid="page-tree-item-overview"
                  >
                    <BookOpen className="w-3.5 h-3.5 flex-shrink-0 text-primary/80" />
                    <span className="flex-1 text-sm truncate">Overview</span>
                  </button>
                )}
                {sessions.map((s) => {
                  const active = selectedSession?.id === s.id;
                  const isDragOver = dragKind === "session" && dragOverId === s.id && dragId !== s.id;
                  return (
                    <div
                      key={s.id}
                      className={`group relative ${isDragOver ? "border-t-2 border-primary" : "border-t-2 border-transparent"}`}
                      draggable
                      onDragStart={(e) => { setDragKind("session"); setDragId(s.id); e.dataTransfer.effectAllowed = "move"; }}
                      onDragOver={(e) => { if (dragKind === "session") { e.preventDefault(); setDragOverId(s.id); } }}
                      onDragLeave={() => { if (dragOverId === s.id) setDragOverId(null); }}
                      onDrop={(e) => { e.preventDefault(); if (dragKind === "session") { reorderSessions(dragId, s.id); } setDragId(null); setDragOverId(null); setDragKind(null); }}
                      onDragEnd={() => { setDragId(null); setDragOverId(null); setDragKind(null); }}
                    >
                      <button
                        onClick={() => setSelectedSession(s)}
                        className={`w-full text-left flex items-center gap-2 px-2 py-1.5 rounded-sm transition-colors ${active ? "bg-primary/15 text-foreground" : "hover:bg-muted/30 text-muted-foreground"} ${dragId === s.id ? "opacity-50" : ""}`}
                        data-testid={`page-tree-item-${s.id}`}
                      >
                        <GripVertical className="w-3 h-3 flex-shrink-0 text-muted-foreground/30 cursor-grab active:cursor-grabbing" data-testid={`session-drag-handle-${s.id}`} />
                        <FileText className={`w-3.5 h-3.5 flex-shrink-0 ${active ? "text-primary" : "text-muted-foreground/60"}`} />
                        <span className="flex-1 text-sm truncate">{s.title}</span>
                        {s.entries?.length > 0 && <span className="text-[10px] text-muted-foreground/50">{s.entries.length}</span>}
                      </button>
                      <button
                        onClick={(e) => { e.stopPropagation(); deleteSession(s); }}
                        className="absolute right-1.5 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 text-muted-foreground/50 hover:text-destructive"
                        title="Delete session"
                        data-testid={`delete-session-${s.id}`}
                      >
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          </ResizablePanel>

          <ResizableHandle withHandle />

          {/* ============== RIGHT PANEL — EDITOR ============== */}
          <ResizablePanel defaultSize={panelSizes[2]} minSize={40}>
            <div className="h-full flex flex-col bg-[#0d0a07]">
              {!selectedStory && (
                <div className="flex-1 flex items-center justify-center">
                  <div className="text-center max-w-md px-6">
                    <NotebookPen className="w-12 h-12 text-primary/30 mx-auto mb-4" />
                    <h2 className="text-2xl font-serif text-foreground mb-2">Begin Your Chronicle</h2>
                    <p className="text-sm text-muted-foreground mb-6">Stories are the notebooks of your campaign. Each one holds the sessions, lore, and notes for a single arc.</p>
                    <Button onClick={openCreateStory} data-testid="create-first-story-btn">
                      <Plus className="w-4 h-4 mr-2" /> Create your first Story
                    </Button>
                  </div>
                </div>
              )}

              {selectedStory && !selectedSession && (
                <div className="flex-1 flex flex-col overflow-hidden" data-testid="story-editor">
                  <div className="px-5 py-3 border-b border-border flex items-center gap-3 flex-wrap">
                    <h2 className="font-serif text-xl text-foreground flex-1 truncate" data-testid="story-editor-title">{selectedStory.title}</h2>
                    <span className="text-[10px] text-muted-foreground/70">{storyContentDirty ? "Saving…" : "Saved"}</span>
                  </div>
                  <div className="flex-1 overflow-hidden p-5 min-h-0">
                    <RichTextEditor
                      value={editingStoryContent}
                      onChange={(v) => { setEditingStoryContent(v); setStoryContentDirty(true); }}
                      placeholder="Begin writing the story…"
                      fillHeight
                    />
                  </div>
                </div>
              )}

              {selectedStory && selectedSession && (
                <div className="flex-1 flex flex-col overflow-hidden" data-testid="session-editor">
                  <div className="px-5 py-3 border-b border-border flex items-center gap-3 flex-wrap">
                    <h2 className="font-serif text-xl text-foreground flex-1 truncate" data-testid="session-editor-title">{selectedSession.title}</h2>
                    <span className="text-[10px] text-muted-foreground/70" data-testid="session-save-indicator">{sessionContentDirty ? "Saving…" : "Saved"}</span>
                    <Button size="sm" variant="outline" disabled={recapBusy || isCommoner} onClick={generateRecap} data-testid="generate-recap-btn">
                      {recapBusy ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Wand2 className="w-3 h-3 mr-1" />}
                      {selectedSession.recap ? "Regenerate Recap" : "AI Recap"}
                    </Button>
                  </div>

                  {/* AI Recap as a slim banner above the rich-text body */}
                  {selectedSession.recap && (
                    <div className="mx-5 mt-3 rounded-sm border border-primary/30 bg-primary/5 p-3" data-testid="session-recap-block">
                      <div className="flex items-center gap-2 mb-1.5 text-primary">
                        <Sparkles className="w-3.5 h-3.5" />
                        <span className="text-xs uppercase tracking-wider font-medium">Chronicle</span>
                      </div>
                      <p className="text-sm leading-relaxed whitespace-pre-wrap text-foreground/90">{selectedSession.recap}</p>
                    </div>
                  )}

                  {/* Main rich-text editor — same component, same toolbar, same autosave behaviour as Chapters */}
                  <div className="flex-1 overflow-hidden p-5 min-h-0">
                    <RichTextEditor
                      value={editingSessionContent}
                      onChange={(v) => { setEditingSessionContent(v); setSessionContentDirty(true); }}
                      placeholder="Write the session — what happened, who showed up, what changed. Use the toolbar for headings, lists, quotes, @mentions…"
                      fillHeight
                    />
                  </div>

                  {/* Quick Log — kept for fast in-play notes, but collapsed by default so it doesn't crowd the main page */}
                  <details className="border-t border-border bg-muted/10" data-testid="session-quicklog-collapsible">
                    <summary className="px-5 py-2 text-xs uppercase tracking-wider text-muted-foreground/80 cursor-pointer hover:bg-muted/20 flex items-center gap-1.5 list-none">
                      <MessageSquarePlus className="w-3 h-3" /> Quick Log
                      {(selectedSession.entries || []).length > 0 && (
                        <span className="text-[10px] text-muted-foreground/60">({(selectedSession.entries || []).length})</span>
                      )}
                    </summary>
                    <div className="px-5 pb-3 space-y-2 max-h-48 overflow-y-auto">
                      {(selectedSession.entries || []).length === 0 && (
                        <p className="text-xs text-muted-foreground/60 italic">No quick notes yet. Use the field below for fast in-play jots.</p>
                      )}
                      {(selectedSession.entries || []).map((e, idx) => (
                        <div key={idx} className="rounded-sm border border-border/50 bg-background/40 p-2" data-testid={`session-entry-${idx}`}>
                          <p className="text-[10px] text-muted-foreground/60 mb-0.5">{e.timestamp ? new Date(e.timestamp).toLocaleString() : ""}</p>
                          <p className="text-sm text-foreground/90 whitespace-pre-wrap">{e.content}</p>
                        </div>
                      ))}
                    </div>
                    <div className="border-t border-border p-2 flex gap-2 bg-background/30">
                      <Input
                        value={sessionEntryDraft}
                        onChange={(e) => setSessionEntryDraft(e.target.value)}
                        onKeyDown={(e) => { if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) addSessionEntry(); }}
                        placeholder="Quick note… (Ctrl+Enter to post)"
                        className="flex-1 h-8"
                        data-testid="session-entry-input"
                      />
                      <Button onClick={addSessionEntry} size="sm" disabled={postingEntry || !sessionEntryDraft.trim()} data-testid="session-entry-post-btn">
                        {postingEntry ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                      </Button>
                    </div>
                  </details>
                </div>
              )}
            </div>
          </ResizablePanel>
        </ResizablePanelGroup>
      </div>

      {/* CREATE DIALOG */}
      <Dialog open={!!createDialog} onOpenChange={(v) => !v && setCreateDialog(null)}>
        <DialogContent className="bg-card border-border max-w-lg" data-testid="story-create-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif text-xl">
              {createDialog === "story" ? "New Story" : "New Session"}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-3 mt-2">
            <div>
              <label className="text-sm font-medium block mb-1">Title</label>
              <Input
                value={createForm.title}
                onChange={e => setCreateForm({ ...createForm, title: e.target.value })}
                placeholder={createDialog === "story" ? "e.g. The Crystal Caves Arc" : `e.g. Session ${sessions.length + 1}`}
                autoFocus
                data-testid="story-create-title"
              />
            </div>
            <Button onClick={createDialog === "story" ? submitCreateStory : submitCreateSession} disabled={creating} className="w-full" data-testid="story-create-save">
              {creating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
              Create {createDialog === "story" ? "Story" : "Session"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </Layout>
  );
};

export default StoryNotebookPage;
